import { Box, Button, Flex, Text } from '@mantine/core'
import clsx from 'clsx'
import { memo } from 'react'

import { useTranslation } from '@flecto/translations'

import { detectArrayElementDifferences } from '../../../../lib/utils'
import {
  type FilterValue,
  type FilterField,
} from '../../components/multi-select/filterTypeConfig'
import { useFilterStore } from '../../store/useFilterStore'
import styles from './AccordionControlHeader.module.css'

type AccordionControlHeaderProps = {
  handleClearFilter: (key: string) => void
  selectedFilters: FilterValue[]
  accordionItemHovered: boolean
  field: FilterField
  isOpen: boolean
  id: string
}

export const AccordionControlHeader = memo(
  ({
    accordionItemHovered,
    handleClearFilter,
    selectedFilters,
    isOpen,
    field,
    id,
  }: AccordionControlHeaderProps): JSX.Element => {
    const { appliedFilters } = useFilterStore(id)
    const { t } = useTranslation('shared', { keyPrefix: 'filters' })
    const selectedFilter = selectedFilters?.find((f) => f.key === field.key)
    const appliedFilter = appliedFilters?.find((f) => f.key === field.key)

    const fieldClearedButWasApplied =
      !selectedFilter?.value && appliedFilter?.value

    const compareValues = (
      value1?: FilterValue['value'],
      value2?: FilterValue['value']
    ): boolean => {
      if (value1 === undefined || value2 === undefined) {
        return value1 !== value2
      }
      if (Array.isArray(value1) && Array.isArray(value2)) {
        const sortedValue1 = [...value1].sort()
        const sortedValue2 = [...value2].sort()
        return detectArrayElementDifferences(sortedValue1, sortedValue2)
      }
      return value1 !== value2
    }

    const showDifferentValueBadge =
      (selectedFilter &&
        compareValues(selectedFilter.value, appliedFilter?.value)) ||
      fieldClearedButWasApplied

    const showAppliedBadge =
      appliedFilter &&
      !compareValues(selectedFilter?.value, appliedFilter.value)

    return (
      <Flex
        pos='relative'
        align='center'
        component='header'
        gap='sm'
        className={clsx(styles.filterAccordionItemControl, {
          [styles.hovered]: accordionItemHovered,
          [styles.isOpen]: isOpen,
        })}
      >
        {field.icon && <div data-testid='field-icon'>{field.icon}</div>}
        <Text
          fw='500'
          className={styles.filterLabel}
          c='gray.7'
          data-testid='field-label'
        >
          {t(field.label || '')}
        </Text>
        <Box data-testid='badges-container'>
          {showDifferentValueBadge && !showAppliedBadge && (
            <Box
              bg='gray.5'
              style={{ borderRadius: '50%' }}
              w='sm'
              h='sm'
              data-testid='selected-badge'
            />
          )}
          {showAppliedBadge && (
            <Box
              bg='green'
              style={{ borderRadius: '50%' }}
              w='sm'
              h='sm'
              data-testid='applied-badge'
            />
          )}
        </Box>
        <Button
          variant='transparent'
          onClick={(event) => {
            event.stopPropagation()
            handleClearFilter(field.key)
          }}
          pos='absolute'
          right='0'
          c='blue.7'
          size='sm'
          classNames={{
            label: styles.filterItemClearBtnLabel,
          }}
          style={{
            zIndex: 10,
            visibility:
              accordionItemHovered && selectedFilter ? 'visible' : 'hidden',
          }}
          data-testid='clear-button'
        >
          {t('accordion.clear')}
        </Button>
      </Flex>
    )
  }
)

AccordionControlHeader.displayName = 'AccordionControlHeader'
